<template>
    <page-shell :title="app?.repository || 'Application'">
        <div v-if="!loading" class="application">
            <div class="detail-actions">
                <font-awesome-icon @click="refreshData" icon="fa fa-rotate-right"
                    class="resize-navbar-icon" />
            </div>
            <x-cubes-filter v-model="filter" :locations="locations"></x-cubes-filter>

            <el-table :data="allXCubes" height="calc(100vh - 400px)" width="100%">
                <el-table-column prop="location.name" label="Location" sortable></el-table-column>
                <el-table-column prop="name" label="Device" width="180" sortable></el-table-column>
                <el-table-column prop="type" label="Type" width="180" sortable></el-table-column>
                <el-table-column v-for="version of allVersions" :key="version" :label="version" width="75">
                    <template slot="header" slot-scope="scope">
                        <el-dropdown trigger="click" @command="handleVersionCommand">
                            <span class="el-dropdown-link">
                                {{ (parseInt(version[0]) === NaN) ? version : 'v' + version }}
                            </span>
                            <el-dropdown-menu slot="dropdown">
                                <el-dropdown-item
                                    :command="{ action: 'activate', version: version }">Activate</el-dropdown-item>
                                <el-dropdown-item
                                    :command="{ action: 'delete', version: version }">Delete</el-dropdown-item>
                            </el-dropdown-menu>
                        </el-dropdown>
                    </template>

                    <template slot-scope="scope">
                        <div v-if="getStatus(scope.row.id, version)">

                            <el-dropdown trigger="click" @command="handleInstanceCommand">
                                <span class="el-dropdown-link">
                                    <span class="dot" :title="getStatus(scope.row.id, version)" :style="{
                                        backgroundColor: getStatusColor(
                                            getStatus(scope.row.id, version)
                                        ),
                                    }"></span>
                                </span>
                                <el-dropdown-menu slot="dropdown">
                                    <el-dropdown-item
                                        v-if="!['pending-active', 'active'].includes(getStatus(scope.row.id, version))"
                                        :command="{ action: 'activate', version: version, xcubeId: scope.row.id }">Activate</el-dropdown-item>
                                    <el-dropdown-item
                                        v-if="['pending-active', 'active'].includes(getStatus(scope.row.id, version))"
                                        :command="{ action: 'deactivate', version: version, xcubeId: scope.row.id }">Deactivate</el-dropdown-item>
                                    <el-dropdown-item
                                        :command="{ action: 'delete', version: version, xcubeId: scope.row.id }">Delete</el-dropdown-item>
                                </el-dropdown-menu>
                            </el-dropdown>
                        </div>
                    </template>
                </el-table-column>
            </el-table>
        </div>
        <spinner :loading="loading" text="Loading applications..."></spinner>
    </page-shell>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router/composables";
import PageShell from "../shared/PageShell.vue";
import Spinner from "../shared/Spinner.vue";
import XCubesFilter from "../XCubesFilter.vue";
import { getXCubeApplications, getApplication, deleteXCubeApplication, deactivateXCubeApplication, getXCubeApplicationsForXCubeAndApplication } from "../../api/graphql/applications";
import { getXCubesSimple } from "../../api/graphql/xcubes";
import { getUserLocationsSimple } from "../../api/graphql/locations";
import { activateApplicationVersion, deleteApplicationVersion } from "../../api/rest";
import { Message, MessageBox } from "element-ui";


const ALL_OPTION = {
    name: "All",
    id: "all",
    display: "All",
};

const filter = ref({
    active: { name: "Active", id: "active" },
    location: ALL_OPTION,
    type: ALL_OPTION,
    country: ALL_OPTION,
});

const route = useRoute();
const app = ref(null);
const xCubeApplications = ref([]);
const allXCubes = ref([]);
const loading = ref(true);
const locations = ref([]);

const getStatusColor = (status) => {
    const colorMap = {
        active: "mediumseagreen",
        "pending-active": "yellow",
        downloading: "orange",
        extracting: "orange",
        downloading_error: "tomato",
        extracting_error: "tomato",
        inactive: "dodgerblue",
    };
    return colorMap[status] || "";
};

const getStatus = (xcubeId, version) => {
    const result = xCubeApplications.value?.find(
        (x) => x.version === version && x.x_cube.id === xcubeId.toString(),
    );
    return result?.status;
};

const handleVersionCommand = async (command) => {
    try {
        if (command.action === "activate") {
            try {
                await activateApplicationVersion(
                    route.params.id,
                    command.version,
                    cleanedFilter.value
                );
            } catch (error) {
                if (error.message.includes("Some xCubes do not have the application inactive")) {
                    try {
                        await MessageBox.confirm(
                            "Some xCubes do not have the application downloaded. Do you want to force activation on all matching xCubes?",
                            "Force Activation",
                            {
                                confirmButtonText: "Yes, FORCE IT",
                                cancelButtonText: "No, Cancel",
                                type: "warning",
                            }
                        );
                        await activateApplicationVersion(
                            route.params.id,
                            command.version,
                            {
                                ...cleanedFilter.value,
                                ignore_missing: true,
                            }
                        );
                        await refreshData();
                    } catch (e) {
                        console.error("Activation cancelled:", e);
                        Message.error("Activation cancelled.");
                    }
                } else {
                    console.log("Error activating application version:", error.message);
                    throw error;
                }
            }
        } else if (command.action === "delete") {
            await deleteApplicationVersion(
                route.params.id,
                command.version,
            );
            await refreshData();
        } else {
            console.log(`Unknown command: ${command}`);
        }
    } catch (error) {
        Message.error(`Error when trying to ${command.action} ${command.version}: ${error.message}`);
    }
};

const handleInstanceCommand = async (command) => {
    console.log("Instance command:", command);
    const {
        xcubeId,
        version,
        action,
    } = command;
    console.log(`Handling command ${action} for xcube ${xcubeId} version ${version}`);
    try {
        const xcubeApplication = xCubeApplications.value.find(
            (x) => x.version === version && x.x_cube.id === xcubeId
        );
        if (!xcubeApplication) {
            throw new Error(`No application found for xcube ${xcubeId} version ${version}`);
        }
        if (action === "activate") {
            await activateApplicationVersion(
                route.params.id,
                version,
                { xcubeId }
            );
        } else if (action === "delete") {
            if (xcubeApplication) {
                await deleteXCubeApplication(xcubeApplication.id);
            }
        } else if (action === "deactivate") {
            if (xcubeApplication) {
                await deactivateXCubeApplication(xcubeApplication.id);
            }
        } else {
            console.log(`Unknown action in command: ${command}`);
        }
        refreshIndividualData(route.params.id, xcubeId);
    } catch (error) {
        Message.error(`Error when trying to ${action} for xcube ${xcubeId} ${version}: ${error.message}`);
    }
};

const allVersions = computed(() => {
    if (xCubeApplications.value.length > 0) {
        const set = xCubeApplications.value?.reduce(
            (acc, x) => acc.add(x.version),
            new Set(),
        );
        const list = Array.from(set);
        return list.toSorted((a, b) => (a > b ? -1 : 1));
    }
    return [];
});

const cleanedFilter = computed(() => {
    const filterValue = {
    };
    if (filter.value.active?.id && filter.value.active.id !== ALL_OPTION.id) {
        filterValue.status = filter.value.active.id;
    }
    if (filter.value.location?.id && filter.value.location.id !== ALL_OPTION.id) {
        filterValue.location = filter.value.location.id;
    }
    if (filter.value.type?.id && filter.value.type.id !== ALL_OPTION.id) {
        filterValue.type = filter.value.type.id;
    }
    if (filter.value.country?.id && filter.value.country.id !== ALL_OPTION.id) {
        filterValue.country = filter.value.country.id;
    }
    return filterValue;
});

const getFilteredDevices = async () => {
    return await getXCubesSimple(cleanedFilter.value);
};

const refreshData = async () => {
    loading.value = true;
    xCubeApplications.value = await getXCubeApplications(route.params.id);
    allXCubes.value = await getFilteredDevices();
    loading.value = false;
};

const refreshIndividualData = async (applicationId, xcubeId) => {
    const result = await getXCubeApplicationsForXCubeAndApplication(xcubeId, applicationId);
    xCubeApplications.value = xCubeApplications.value.map((x) => {
        const replacement = result.find((r) => r.id === x.id);
        return replacement ? replacement : x;
    });
};

onMounted(async () => {
    loading.value = true;
    const result = await Promise.all([
        getApplication(route.params.id),
        getFilteredDevices(),
        getXCubeApplications(route.params.id),
        getUserLocationsSimple(),
    ]);
    [app.value, allXCubes.value, xCubeApplications.value, locations.value] = result;
    loading.value = false;
});

watch(
    () => filter,
    async () => {
        loading.value = true;
        allXCubes.value = await getFilteredDevices();
        loading.value = false;
    },
    { deep: true }
);

</script>

<style scoped lang="scss">
@use "../../colors" as *;

.application {
    width: 100%;
    min-width: 0;
}

.detail-actions {
    margin-bottom: 0.75rem;
}

.resize-navbar-icon {
    cursor: pointer;
}

.dot {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 10px;
    margin-right: 5px;
}
</style>